import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { plans as plansApi } from '../api/client.js';
import NotificationBell from '../notifications/NotificationBell.jsx';
import OfflineIndicator from '../offline/OfflineIndicator.jsx';
import ImpersonationBanner from '../components/ImpersonationBanner.jsx';
import { LogoutIcon, MenuIcon, BikeIcon } from './iconsExtra.jsx';
import LayoutSidebar from './LayoutSidebar.jsx';
import { buildNavItems } from './layoutNav.js';

export default function Layout() {
  const { user, campaigns, activeCampaign, switchCampaign, logout, viewAsRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [fieldMode, setFieldMode] = useState(false);

  const campaignEntry = campaigns.find((c) => c.campaignId === activeCampaign);
  const realRole = campaignEntry?.role;
  const effectiveRole = viewAsRole || realRole;

  const isCoordinator = effectiveRole === 'coordinator' || effectiveRole === 'admin'
    || user?.globalRole === 'superadmin';
  const isAdmin = effectiveRole === 'admin' || (user?.globalRole === 'superadmin' && !viewAsRole);
  const isPartner = effectiveRole === 'partner';

  const navItems = buildNavItems({ fieldMode, effectiveRole, isAdmin, isCoordinator, isPartner });

  /** Toggle field/office mode. */
  const handleFieldMode = async () => {
    if (fieldMode) {
      setFieldMode(false);
      navigate('/');
      return;
    }
    try {
      const allPlans = await plansApi.list();
      const activePlan = allPlans.find((p) => p.status === 'active' || p.status === 'draft');
      setFieldMode(true);
      navigate(activePlan ? `/delivery/${activePlan.id}` : '/plans');
    } catch (_fetchErr) {
      setFieldMode(true);
      navigate('/plans');
    }
  };

  const userInitials = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <OfflineIndicator />
      <ImpersonationBanner />

      {/* Header */}
      <header className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] text-white px-3 py-2.5 md:px-6 md:py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 no-underline text-white min-w-0">
            <BikeIcon size={22} className="text-cyan-400 flex-shrink-0" />
            <span className="font-bold text-base tracking-tight truncate">Velo</span>
            {fieldMode && (
              <span className="text-[9px] bg-orange-500 px-1.5 py-0.5 rounded-full font-semibold uppercase flex-shrink-0">
                Terrain
              </span>
            )}
          </Link>

          <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
            {campaigns.length > 1 && (
              <select
                value={activeCampaign || ''}
                onChange={(e) => switchCampaign(e.target.value)}
                className="bg-white/10 text-white text-[11px] rounded-lg px-2 py-1.5 border border-white/20 max-w-[100px] md:max-w-none md:text-xs md:px-3"
              >
                {campaigns.map((c) => (
                  <option key={c.campaignId} value={c.campaignId} className="text-gray-900">
                    {c.campaignName}
                  </option>
                ))}
              </select>
            )}
            {isCoordinator && (
              <button
                onClick={handleFieldMode}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg font-medium border md:text-xs md:px-3 ${
                  fieldMode
                    ? 'bg-orange-500 border-orange-400 text-white'
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
              >
                {fieldMode ? 'Bureau' : 'Terrain'}
              </button>
            )}
            <NotificationBell />
            <button
              onClick={() => navigate('/profile')}
              className="relative"
              aria-label="Profil"
            >
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-cyan-600 flex items-center justify-center text-[10px] md:text-xs font-bold hover:ring-2 hover:ring-cyan-400 transition-all cursor-pointer">
                {userInitials}
              </div>
              {viewAsRole && (
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-amber-500 rounded-full border-2 border-[#1e293b] flex items-center justify-center text-[7px] font-bold text-white">
                  {viewAsRole[0].toUpperCase()}
                </span>
              )}
            </button>
            <button
              onClick={logout}
              className="hidden md:flex items-center gap-1 text-xs text-white/70 hover:text-white"
              aria-label="Déconnexion"
            >
              <LogoutIcon size={16} />
              <span>Sortir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Desktop sidebar + content */}
      <div className="flex">
        <LayoutSidebar navItems={navItems} currentPath={location.pathname} onLogout={logout} />
        <main className="flex-1 p-4 pb-24 md:pb-6 md:p-6 animate-fade-in">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-stretch h-16 z-50 shadow-[0_-2px_10px_rgb(0_0_0/0.05)]">
        {navItems.slice(0, 5).map((item) => {
          const active = location.pathname === item.path
            || (item.path !== '/' && location.pathname.startsWith(item.path + '/'));
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 min-w-[44px] min-h-[44px] gap-0.5 ${
                active ? 'text-[var(--color-accent)]' : 'text-[var(--color-neutral)]'
              }`}
            >
              <span className={`p-1 rounded-lg ${active ? 'bg-blue-50' : ''}`}>
                <item.Icon size={20} />
              </span>
              <span className="text-[10px] font-medium leading-tight">{item.label}</span>
            </Link>
          );
        })}
        {navItems.length > 5 && (
          <Link
            to={navItems[5].path}
            className={`flex flex-col items-center justify-center flex-1 min-w-[44px] min-h-[44px] gap-0.5 text-[var(--color-neutral)]`}
          >
            <span className="p-1"><MenuIcon size={20} /></span>
            <span className="text-[10px] font-medium leading-tight">Plus</span>
          </Link>
        )}
        <button
          onClick={logout}
          className="flex flex-col items-center justify-center min-w-[44px] min-h-[44px] gap-0.5 text-[var(--color-neutral)]"
          aria-label="Déconnexion"
        >
          <span className="p-1"><LogoutIcon size={20} /></span>
          <span className="text-[10px] font-medium leading-tight">Sortir</span>
        </button>
      </nav>
    </div>
  );
}
